import type { MutableRefObject } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGenerateZkProofMutation } from '@modules/docs-verification-widget/api';

import type { FinishStepProps, ZkProof } from '../types';

type UseGetZkProofParams = {
    verified?: boolean | null;
    apiUrl?: string;
    shortId: string;
    getZkProof?: FinishStepProps['getZkProof'];
    timer: MutableRefObject<ReturnType<typeof setTimeout> | undefined>;
    setIsLoading: (loading: boolean) => void;
    isCustomPartner?: boolean;
};

export const useGetZkProof = ({
    verified,
    apiUrl,
    shortId,
    getZkProof,
    timer,
    setIsLoading,
    isCustomPartner,
}: UseGetZkProofParams) => {
    const { t } = useTranslation();
    const [generateZkProof] = useGenerateZkProofMutation();
    const [zkProof, setZkProof] = useState<ZkProof>();
    const [zkProofError, setZkProofError] = useState('');

    useEffect(() => {
        if (isCustomPartner || verified == null) {
            return;
        }

        if (timer.current) {
            clearInterval(timer.current);
        }

        if (verified) {
            generateZkProof({ url: apiUrl as string, body: { short_id: shortId } })
                .unwrap()
                .then((response) => {
                    getZkProof?.(response?.proof);
                    setZkProof(response?.proof);
                    setIsLoading(false);
                })
                .catch((error) => {
                    switch (error?.data?.error_level) {
                        case 'needs_reverification':
                            switch (error?.data?.message) {
                                case 'Proof does not match the current profile':
                                    setZkProofError(
                                        t(
                                            'docVerificationWidget.zkProof.error.mismatchedProfile',
                                        ),
                                    );
                                    break;
                                case 'Proof has expired':
                                    setZkProofError(
                                        t('docVerificationWidget.zkProof.error.expired'),
                                    );
                                    break;
                                default:
                                    setZkProofError(
                                        t('docVerificationWidget.verificationError.subTitle'),
                                    );
                            }

                            break;
                        case 'issuance_restricted':
                            setZkProofError(
                                t('docVerificationWidget.zkProof.error.alreadyUsed'),
                            );
                            break;
                        default:
                            setZkProofError(
                                t('docVerificationWidget.verificationError.subTitle'),
                            );
                    }
                    setIsLoading(false);
                });
        } else {
            setZkProofError(t('docVerificationWidget.verificationError.subTitle'));
            setIsLoading(false);
        }
    }, [
        apiUrl,
        generateZkProof,
        getZkProof,
        verified,
        shortId,
        t,
        isCustomPartner,
        setIsLoading,
        timer,
    ]);

    return { zkProof, zkProofError };
};
